// SPDX-License-Identifier: GPL-3.0-or-later
// The game: phases, balls, score, inputs and the bridge to the table rules.
//
// This class replaces the table script of the upstream project. It owns the player
// (physics loop), turns item events into `PhysicsEvent` values for the rules,
// and implements `RulesContext` for the rules. It has no clock and no `Math.random`.
// Game time moves only when `step()` runs physics steps of 1 ms.

import {
  BALLS_PER_GAME,
  type BallFrame,
  type EngineEvent,
  type EngineOptions,
  type GamePhase,
  type GameStatus,
  type InputEvent,
  type LightState,
  NUDGE_COOLDOWN_MS,
  type PhysicsEvent,
  type PinballEngine,
  type RenderFrame,
  type RulesContext,
  type TableRules,
  type TableSpec,
} from "../../shared/contracts";
import type { Ball } from "../items/ball/ball";
import { Vertex3D } from "../math/vertex3d";
import { Table } from "../table-model";
import { Event } from "./event";
import type { EventProxy, EventSink } from "./event-proxy";
import { PinInput } from "./pin-input";
import { Player } from "./player";
import type { IBallCreationPosition } from "./player-physics";
import { Rng } from "./rng";
import { clampExtraBalls, type RulesExtension, rulesExtension } from "./rules-ext";

/** The most real time that one `step()` call advances, in ms. */
export const MAX_STEP_MS = 100;

/** The pause between the end of a ball and the start of the next one, in game ms. */
export const BALL_END_PAUSE_MS = 1000;

/** The velocity that a nudge adds to every ball, in table units per physics time unit. */
export const NUDGE_SPEED = 1.0;

/**
 * The item name of a tilt event. The rules read it as a `PhysicsEvent` with ball id 0.
 * A nudge that comes while the nudge cooldown runs is a tilt event.
 */
export const TILT_ITEM = "Tilt";

/** The highest score multiplier. */
const MAX_MULTIPLIER = 10;

/** The largest number of balls in one game that the engine accepts. */
const MAX_BALLS_PER_GAME = 99;

/** The physics step in ms. The ported collision code is tuned for 1 ms steps. */
const STEP_MS = 1;

/** Check the engine options. Return the number of balls in one game. */
function checkOptions(opts: EngineOptions): number {
  if (opts.physicsStepMs !== undefined && opts.physicsStepMs !== STEP_MS) {
    throw new RangeError(
      `physicsStepMs must be ${STEP_MS}. The collision code is tuned for 1 ms steps.`,
    );
  }
  const balls = opts.ballsPerGame ?? BALLS_PER_GAME;
  if (!Number.isInteger(balls) || balls < 1 || balls > MAX_BALLS_PER_GAME) {
    throw new RangeError(`ballsPerGame must be a whole number from 1 to ${MAX_BALLS_PER_GAME}.`);
  }
  return balls;
}

/** A creator that puts a ball at a fixed point and gives it no speed. */
class FixedStart implements IBallCreationPosition {
  private readonly pos: Vertex3D;

  constructor(pos: Vertex3D) {
    this.pos = pos;
  }

  public getBallCreationPosition(): Vertex3D {
    return this.pos.clone();
  }

  public getBallCreationVelocity(): Vertex3D {
    return new Vertex3D(0, 0, 0);
  }

  public onBallCreated(): void {
    // nothing to do
  }
}

/**
 * One game on one table. It implements the engine API of the contracts.
 */
export class Game implements PinballEngine, EventSink {
  public readonly table: TableSpec;

  private readonly model: Table;
  private readonly rules: TableRules;
  private readonly player: Player;
  private readonly rng: Rng;
  private readonly pinInput = new PinInput();
  private readonly ballsPerGame: number;
  private readonly ext: RulesExtension | null;
  private readonly listeners = new Set<(ev: EngineEvent) => void>();
  private readonly outbox: EngineEvent[] = [];
  private readonly pending: PhysicsEvent[] = [];
  private readonly drainBalls = new WeakMap<PhysicsEvent, Ball>();
  private readonly lightModes = new Map<string, LightState>();
  private readonly targetTags = new Map<string, readonly string[]>();
  private readonly modeLabels = new Map<string, string>();
  private readonly ctx: RulesContext;

  private phase: GamePhase = "attract";
  private timeMs = 0;
  private stepRemainder = 0;
  private score = 0;
  private ballNumber = 0;
  private multiplier = 1;
  private activeModes: string[] = [];
  private saveEndMs = -1;
  private gameStartMs = 0;
  private gameDurationMs = 0;
  private nextBallAtMs = 0;
  private lastNudgeMs = Number.NEGATIVE_INFINITY;
  private plungerHeld = false;
  private multiballActive = false;
  private disposed = false;
  /** The reason of an `endBall` call that the engine has not run yet. */
  private forcedEnd: string | undefined;

  constructor(spec: TableSpec, rules: TableRules, opts: EngineOptions) {
    this.ballsPerGame = checkOptions(opts);
    this.table = spec;
    this.rules = rules;
    this.ext = rulesExtension(rules);
    this.rng = new Rng(opts.seed);
    this.model = Table.fromSpec(spec);
    this.player = new Player(this.model, this.rng, this).init();
    this.ctx = this.createContext();
    this.indexNames(spec, rules);
  }

  /** Keep the start state of each light, the tags of each target and the label of each mode. */
  private indexNames(spec: TableSpec, rules: TableRules): void {
    for (const light of spec.lights) {
      this.lightModes.set(light.name, light.state ?? 0);
    }
    for (const target of spec.targets) {
      this.targetTags.set(target.name, target.tags ?? []);
    }
    for (const mode of rules.modes) {
      this.modeLabels.set(mode.id, mode.label);
    }
  }

  // ───────────────────────────── PinballEngine ─────────────────────────────

  public start(): void {
    if (this.disposed) {
      return;
    }
    this.startGame();
    this.flush();
  }

  /** Starts a game. It does not deliver events. `step()` and `start()` deliver them. */
  private startGame(): void {
    if (this.phase !== "attract" && this.phase !== "game_over") {
      return;
    }
    this.pinInput.clear();
    for (const ball of [...this.player.getBalls()]) {
      this.player.destroyBall(ball);
    }
    this.pending.length = 0;
    this.forcedEnd = undefined;
    this.score = 0;
    this.ballNumber = 0;
    this.multiplier = 1;
    this.activeModes = [];
    this.saveEndMs = -1;
    this.multiballActive = false;
    this.gameStartMs = this.timeMs;
    this.gameDurationMs = 0;
    this.phase = "ball_ready";
    this.resetTable();
    this.emit({ type: "game_start", ballsPerGame: this.ballsPerGame });
    this.rules.onGameStart(this.ctx);
    this.beginBall();
    this.settle();
  }

  public input(ev: InputEvent): void {
    if (this.disposed) {
      return;
    }
    this.pinInput.push(ev);
  }

  public step(dtMs: number): RenderFrame {
    if (this.disposed) {
      return { timeMs: this.timeMs, balls: [], items: [] };
    }
    const dt = Number.isFinite(dtMs) ? Math.min(MAX_STEP_MS, Math.max(0, dtMs)) : 0;
    this.stepRemainder += dt;
    const ticks = Math.floor(this.stepRemainder / STEP_MS);
    this.stepRemainder -= ticks * STEP_MS;

    let ran = 0;
    while (ran < ticks && !this.isHeld()) {
      this.tick();
      ran++;
    }
    if (this.ext && this.isHeld()) {
      // The rules hold play: no physics step, no game time. The rules get only the time of the
      // frame that the ticks above did not use.
      this.ext.onHoldTick(Math.max(0, dt - ran * STEP_MS));
      this.stepRemainder = 0;
    }
    const elapsed = ran * STEP_MS;
    this.player.updateAnimations(this.timeMs);
    if (elapsed > 0 && this.isGameActive()) {
      this.rules.onTick(this.ctx, elapsed);
      this.settle();
    }
    const frame = this.buildFrame();
    this.flush();
    return frame;
  }

  public status(): GameStatus {
    return {
      phase: this.phase,
      score: this.score,
      ballNumber: this.ballNumber,
      ballsPerGame: this.totalBalls(),
      ballsInPlay: this.player.getBalls().length,
      multiplier: this.multiplier,
      activeModes: [...this.activeModes],
      ballSaveMsLeft: Math.max(0, Math.round(this.saveEndMs - this.timeMs)),
      elapsedMs: this.elapsedMs(),
    };
  }

  public on(listener: (ev: EngineEvent) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  public dispose(): void {
    this.disposed = true;
    this.listeners.clear();
    this.outbox.length = 0;
    this.pending.length = 0;
    this.pinInput.clear();
  }

  // ───────────────────────────── the loop ─────────────────────────────

  /** One physics step of 1 ms. */
  private tick(): void {
    this.pinInput.processKeys((ev) => this.handleInput(ev));
    this.player.updatePhysics();
    this.timeMs += STEP_MS;
    this.dispatchEvents();
    if (this.phase === "ball_end" && this.timeMs >= this.nextBallAtMs) {
      this.beginBall();
    }
  }

  /** True while the rules hold play of an active game. */
  private isHeld(): boolean {
    return this.ext !== null && this.isGameActive() && this.ext.holdActive();
  }

  /** The balls of the game: the base balls and the extra balls of the rules. */
  private totalBalls(): number {
    const extra = this.ext ? clampExtraBalls(this.ext.extraBallsEarned()) : 0;
    return Math.min(MAX_BALLS_PER_GAME, this.ballsPerGame + extra);
  }

  private isGameActive(): boolean {
    return this.phase === "ball_ready" || this.phase === "playing" || this.phase === "ball_end";
  }

  private elapsedMs(): number {
    if (this.phase === "attract") {
      return 0;
    }
    if (this.phase === "game_over") {
      return this.gameDurationMs;
    }
    return this.timeMs - this.gameStartMs;
  }

  private buildFrame(): RenderFrame {
    const balls: BallFrame[] = this.player.getBalls().map((b) => b.state.toBallFrame(b.id));
    return { timeMs: this.timeMs, balls, items: this.player.popItemFrames() };
  }

  /** Puts every light and every drop target back to its start state. */
  private resetTable(): void {
    for (const light of this.table.lights) {
      this.applyLight(light.name, light.state ?? 0);
    }
    for (const target of Object.values(this.model.hitTargets)) {
      target.raise(this.timeMs, this.model);
    }
  }

  // ───────────────────────────── events ─────────────────────────────

  /** Called by the item event proxies in the middle of a physics step. */
  public onItemEvent(proxy: EventProxy, event: Event, _params: readonly unknown[]): void {
    const type = this.mapEvent(event);
    if (!type) {
      return;
    }
    const ball = this.player.getActiveBall() ?? this.player.getBalls()[0];
    const name = proxy.getName();
    const base = {
      item: name,
      kind: proxy.kind,
      ballId: ball ? ball.id : 1,
      timeMs: this.timeMs,
      speed: ball ? ball.hit.vel.length() : 0,
    };
    if (
      type === "hit" &&
      proxy.kind === "kicker" &&
      this.model.kickers[name]?.getRole() === "drain"
    ) {
      const drain: PhysicsEvent = { ...base, type: "drain" };
      if (ball) {
        this.drainBalls.set(drain, ball);
      }
      this.pending.push(drain);
      return;
    }
    this.pending.push({ ...base, type });
  }

  private mapEvent(event: Event): PhysicsEvent["type"] | undefined {
    switch (event) {
      case Event.HitEventsHit:
        return "hit";
      case Event.HitEventsUnhit:
        return "unhit";
      case Event.SurfaceEventsSlingshot:
        return "slingshot";
      case Event.SpinnerEventsSpin:
        return "spin";
      default:
        return undefined;
    }
  }

  /** Hands the queued events of the last step to the rules. */
  private dispatchEvents(): void {
    if (this.pending.length === 0) {
      return;
    }
    const batch = this.pending.splice(0);
    for (const ev of batch) {
      if (ev.type === "drain") {
        const ball = this.drainBalls.get(ev);
        if (ball) {
          this.removeBall(ball);
        }
      }
      if (this.isGameActive()) {
        this.rules.onEvent(this.ctx, ev);
        this.settle();
      }
      if (ev.type === "drain") {
        this.afterDrain();
      }
    }
  }

  private emit(ev: EngineEvent): void {
    this.outbox.push(ev);
  }

  /** Delivers the queued engine events. A listener that throws does not stop the others. */
  private flush(): void {
    if (this.outbox.length === 0) {
      return;
    }
    const events = this.outbox.splice(0);
    let failure: unknown;
    let failed = false;
    for (const ev of events) {
      for (const listener of [...this.listeners]) {
        try {
          listener(ev);
        } catch (err) {
          if (!failed) {
            failed = true;
            failure = err;
          }
        }
      }
    }
    if (failed) {
      throw failure;
    }
  }

  // ───────────────────────────── inputs ─────────────────────────────

  private handleInput(ev: InputEvent): void {
    switch (ev.action) {
      case "left_flipper":
        this.handleFlipper("left", ev.pressed);
        break;
      case "right_flipper":
        this.handleFlipper("right", ev.pressed);
        break;
      case "plunger":
        this.handlePlunger(ev.pressed, ev.value);
        break;
      case "nudge_left":
        this.handleNudge(ev.pressed, -NUDGE_SPEED, 0);
        break;
      case "nudge_right":
        this.handleNudge(ev.pressed, NUDGE_SPEED, 0);
        break;
      case "nudge_up":
        this.handleNudge(ev.pressed, 0, -NUDGE_SPEED);
        break;
      case "start":
        if (ev.pressed) {
          this.startGame();
        }
        break;
    }
  }

  private handleFlipper(side: "left" | "right", pressed: boolean): void {
    for (const flipper of Object.values(this.model.flippers)) {
      if (flipper.getSide() !== side) {
        continue;
      }
      if (pressed) {
        flipper.rotateToEnd();
      } else {
        flipper.rotateToStart();
      }
      if (pressed && (this.phase === "ball_ready" || this.phase === "playing")) {
        // The rules read a button press as an event with ball id 0.
        this.pending.push({
          type: "hit",
          item: flipper.getName(),
          kind: "flipper",
          ballId: 0,
          timeMs: this.timeMs,
          speed: 0,
        });
      }
    }
  }

  private handlePlunger(pressed: boolean, value: number | undefined): void {
    if (this.phase !== "ball_ready" && this.phase !== "playing") {
      return;
    }
    const plunger = this.model.plunger;
    if (pressed) {
      if (value !== undefined) {
        plunger.holdAt(value);
        this.plungerHeld = true;
      } else if (!this.plungerHeld) {
        plunger.pullBack();
        this.plungerHeld = true;
      }
      return;
    }
    if (!this.plungerHeld) {
      return;
    }
    this.plungerHeld = false;
    plunger.fire();
    if (this.phase === "ball_ready") {
      this.phase = "playing";
    }
  }

  private handleNudge(pressed: boolean, dx: number, dy: number): void {
    if (!pressed || (this.phase !== "playing" && this.phase !== "ball_ready")) {
      return;
    }
    if (this.timeMs - this.lastNudgeMs < NUDGE_COOLDOWN_MS) {
      // A nudge in the cooldown moves no ball. The rules get a tilt event with ball id 0.
      // The score never goes down for a tilt. The rules decide what a tilt does.
      this.pending.push({
        type: "hit",
        item: TILT_ITEM,
        kind: "decoration",
        ballId: 0,
        timeMs: this.timeMs,
        speed: 0,
      });
      return;
    }
    this.lastNudgeMs = this.timeMs;
    for (const ball of this.player.getBalls()) {
      if (!ball.state.isFrozen) {
        ball.hit.vel.x += dx;
        ball.hit.vel.y += dy;
      }
    }
  }

  // ───────────────────────────── balls and phases ─────────────────────────────

  /** Where a new ball appears: the start point, lifted onto the plunger tip when it is inside the plunger. */
  private startPosition(): Vertex3D {
    const start = this.table.ballStart;
    const radius = this.model.data.ballRadius;
    const mover = this.model.plunger.getMover();
    let y = start.y;
    const lane = (mover.x + mover.x2) * 0.5;
    const half = (mover.x2 - mover.x) * 0.5;
    if (Math.abs(start.x - lane) <= half + radius && y > mover.pos - radius) {
      y = mover.pos - (radius + 0.01);
    }
    // Do not stack two balls at one point.
    for (let i = 0; i < 16; i++) {
      const crowded = this.player.getBalls().some((b) => {
        const dx = b.state.pos.x - start.x;
        const dy = b.state.pos.y - y;
        return dx * dx + dy * dy < 4 * radius * radius;
      });
      if (!crowded) {
        break;
      }
      y -= 2 * radius + 1;
    }
    const z = Math.max(start.z - radius, this.model.getTableHeight());
    return new Vertex3D(start.x, y, z);
  }

  private spawnBallAtStart(): Ball {
    return this.player.createBall(new FixedStart(this.startPosition()));
  }

  private beginBall(): void {
    this.ballNumber++;
    this.multiplier = 1;
    this.saveEndMs = -1;
    this.plungerHeld = false;
    this.spawnBallAtStart();
    this.phase = "ball_ready";
    this.emit({ type: "ball_start", ballNumber: this.ballNumber });
    this.rules.onBallStart(this.ctx);
    if (this.table.plunger.autoPlunger === true) {
      this.model.plunger.fire();
      this.phase = "playing";
    }
  }

  private removeBall(ball: Ball): void {
    if (!this.player.getBalls().includes(ball)) {
      return;
    }
    for (const kicker of Object.values(this.model.kickers)) {
      const hit = kicker.getKickerHit();
      if (hit.ball === ball) {
        hit.ball = undefined;
      }
    }
    this.player.destroyBall(ball);
    this.updateMultiball();
  }

  private updateMultiball(): void {
    const count = this.player.getBalls().length;
    const active = count > 1;
    if (active || this.multiballActive) {
      this.emit({ type: "multiball", ballsInPlay: count, active });
    }
    this.multiballActive = active;
  }

  /** Decides what happens when a ball is lost: nothing, a save, or the end of the ball. */
  private afterDrain(): void {
    if (this.player.getBalls().length > 0 || !this.isGameActive() || this.phase === "ball_end") {
      return;
    }
    if (this.saveEndMs > this.timeMs) {
      this.saveEndMs = -1;
      this.emit({ type: "ball_saved", ballNumber: this.ballNumber });
      this.plungerHeld = false;
      this.spawnBallAtStart();
      this.phase = "ball_ready";
      if (this.table.plunger.autoPlunger === true) {
        this.model.plunger.fire();
        this.phase = "playing";
      }
      return;
    }
    this.endBall();
  }

  /**
   * Runs an `endBall` call of the rules. The rules call it in the middle of a rules callback.
   * The engine drains every ball in play and ends the ball with no bonus. Ball save does not apply.
   */
  private settle(): void {
    if (this.forcedEnd === undefined) {
      return;
    }
    this.forcedEnd = undefined;
    if (this.phase !== "ball_ready" && this.phase !== "playing") {
      return;
    }
    for (const ball of [...this.player.getBalls()]) {
      this.removeBall(ball);
    }
    this.endBall(true);
  }

  private endBall(forced = false): void {
    this.phase = "ball_end";
    const result = this.rules.onBallEnd(this.ctx);
    const bonus =
      !forced && Number.isFinite(result.bonus) && result.bonus > 0 ? Math.floor(result.bonus) : 0;
    this.awardScore(bonus, "bonus");
    for (const modeId of [...this.activeModes]) {
      this.finishMode(modeId, false, 0);
    }
    const ballsLeft = Math.max(0, this.totalBalls() - this.ballNumber);
    this.emit({ type: "ball_lost", ballNumber: this.ballNumber, ballsLeft, bonus });
    if (ballsLeft === 0) {
      this.gameDurationMs = this.timeMs - this.gameStartMs;
      this.phase = "game_over";
      this.emit({
        type: "game_over",
        finalScore: this.score,
        ballsPlayed: this.ballNumber,
        durationMs: this.gameDurationMs,
      });
    } else {
      this.nextBallAtMs = this.timeMs + BALL_END_PAUSE_MS;
    }
  }

  // ───────────────────────────── rules context ─────────────────────────────

  private awardScore(points: number, reason: string, item?: string): void {
    if (!Number.isFinite(points) || points <= 0 || !this.isGameActive()) {
      return;
    }
    const whole = Math.floor(points);
    if (whole <= 0) {
      return;
    }
    this.score = Math.min(Number.MAX_SAFE_INTEGER, this.score + whole);
    this.emit(
      item === undefined
        ? { type: "score", points: whole, total: this.score, reason }
        : { type: "score", points: whole, total: this.score, reason, item },
    );
  }

  private finishMode(modeId: string, completed: boolean, bonus: number): void {
    const index = this.activeModes.indexOf(modeId);
    if (index < 0) {
      return;
    }
    this.activeModes.splice(index, 1);
    // The bonus is only reported. The rules pay a mode award with `addScore`.
    this.emit({ type: "mode_end", modeId, completed, bonus });
  }

  private applyLight(name: string, state: LightState): void {
    const light = this.model.lights[name];
    if (!light || (state !== 0 && state !== 1 && state !== 2)) {
      return;
    }
    if (this.lightModes.get(name) === state) {
      return;
    }
    this.lightModes.set(name, state);
    light.setMode(state, this.timeMs);
    this.emit({ type: "light", name, state });
  }

  private kickBall(kickerName: string, angleDeg?: number, speed?: number): void {
    const kicker = this.model.kickers[kickerName];
    if (!kicker?.holdsBall()) {
      return;
    }
    const eject = kicker.getEject();
    kicker
      .getKickerHit()
      .kickXyz(
        this.model,
        this.player.getPhysics(),
        angleDeg ?? eject.angle,
        speed ?? eject.speed,
        0,
      );
  }

  private addBallFrom(from: string): void {
    const kicker = this.model.kickers[from];
    if (kicker && !kicker.holdsBall()) {
      const center = kicker.getCenter();
      const base = this.model.getSurfaceHeight(kicker.getSurface(), center.x, center.y);
      const ball = this.player.createBall(new FixedStart(new Vertex3D(center.x, center.y, base)));
      // A new ball in a kicker is captured at once, without a hit event.
      kicker
        .getKickerHit()
        .doCollide(this.player.getPhysics(), ball, new Vertex3D(0, 0, 1), true, true);
    } else {
      this.spawnBallAtStart();
    }
    this.updateMultiball();
  }

  private createContext(): RulesContext {
    // The context object uses getters. They need the game in a closure.
    const game = this;
    return {
      get timeMs() {
        return game.timeMs;
      },
      get ballNumber() {
        return game.ballNumber;
      },
      get ballsInPlay() {
        return game.player.getBalls().length;
      },
      addScore: (points, reason, item) => game.awardScore(points, reason, item),
      startMode: (modeId) => game.startRulesMode(modeId),
      endMode: (modeId, completed, bonus) =>
        game.finishMode(modeId, completed, Number.isFinite(bonus) ? Math.max(0, bonus) : 0),
      setLight: (name, state) => game.applyLight(name, state),
      kick: (kicker, angleDeg, speed) => game.kickBall(kicker, angleDeg, speed),
      addBall: (from) => game.addBallFrom(from),
      ballSave: (durationMs) => game.extendBallSave(durationMs),
      resetTargets: (tag) => game.raiseTargets(tag),
      animate: (decoration, animation) => game.emit({ type: "animate", decoration, animation }),
      setMultiplier: (value) => game.setRulesMultiplier(value),
      endBall: (reason) => game.requestEndBall(reason),
    };
  }

  /** The rules start a mode. A mode that already runs does not start again. */
  private startRulesMode(modeId: string): void {
    if (this.activeModes.includes(modeId)) {
      return;
    }
    this.activeModes.push(modeId);
    this.emit({ type: "mode_start", modeId, label: this.modeLabels.get(modeId) ?? modeId });
  }

  /** The rules ask for a ball save. The save end time only grows. */
  private extendBallSave(durationMs: number): void {
    if (Number.isFinite(durationMs) && durationMs > 0) {
      this.saveEndMs = Math.max(this.saveEndMs, this.timeMs + durationMs);
    }
  }

  /** The rules raise every target that has the tag. */
  private raiseTargets(tag: string): void {
    for (const [name, tags] of this.targetTags) {
      if (tags.includes(tag)) {
        this.model.hitTargets[name]?.raise(this.timeMs, this.model);
      }
    }
  }

  /** The rules set the score multiplier. A value that is not finite does nothing. */
  private setRulesMultiplier(value: number): void {
    if (Number.isFinite(value)) {
      this.multiplier = Math.min(MAX_MULTIPLIER, Math.max(1, Math.floor(value)));
    }
  }

  /** The rules end the ball. Only the first call while a ball runs has an effect. */
  private requestEndBall(reason: string): void {
    const running = this.phase === "ball_ready" || this.phase === "playing";
    if (running && this.forcedEnd === undefined) {
      this.forcedEnd = reason;
    }
  }
}
